import React from "react";
import { Instagram, Mail, ShoppingCart, BookOpen } from "lucide-react";
import Logo from "@/components/site/Logo";

function TikTokIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12V9.79a5.7 5.7 0 0 0-.77-.05A5.68 5.68 0 1 0 15.54 15.4V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.28 4.28 0 0 1-3.24-1.48z" />
    </svg>
  );
}

const quick = [
  { label: "Home", href: "#top" },
  { label: "About", href: "#about" },
  { label: "Books", href: "#books" },
  { label: "Coming Soon", href: "#coming-soon" },
  { label: "Reviews", href: "#reviews" },
  { label: "Newsletter", href: "#newsletter" },
  { label: "Contact", href: "#contact" },
];

export default function Footer({ config }) {
  const email = config?.contact_email || "info@katemercerbooks.com";
  const amazon = config?.amazon_url || "https://amzn.eu/d/0hblgrya";
  const goodreads = config?.goodreads_author_url || "#";
  const social = [
    { name: "Instagram", url: config?.instagram_url || "#", Icon: Instagram, ext: true, testid: "footer-instagram" },
    { name: "TikTok", url: config?.tiktok_url || "#", Icon: TikTokIcon, ext: true, testid: "footer-tiktok" },
    { name: "Amazon", url: amazon, Icon: ShoppingCart, ext: true, testid: "footer-amazon" },
    { name: "Goodreads", url: goodreads, Icon: BookOpen, ext: true, testid: "footer-goodreads" },
    { name: "Email", url: `mailto:${email}`, Icon: Mail, ext: false, testid: "footer-email-icon" },
  ];

  return (
    <footer className="bg-[#0A0A0A] text-white pt-24 pb-10 grain relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 relative z-10">
        <div className="flex flex-col items-center text-center pb-14 border-b border-white/10">
          <a href="#top" className="inline-flex mb-6 transition-transform duration-300 hover:scale-[1.03] hover:drop-shadow-[0_4px_18px_rgba(212,175,55,0.5)]" data-testid="footer-logo" aria-label="Kate Mercer Books — home">
            <Logo variant="light" className="h-14 sm:h-16" />
          </a>
          <p className="text-gray-400 max-w-xl text-[15px] leading-relaxed">
            Helping readers think better, study smarter and build successful careers in an AI-powered world.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {social.map((s) => {
              const Icon = s.Icon;
              return (
                <a
                  key={s.name}
                  href={s.url}
                  target={s.ext ? "_blank" : undefined}
                  rel={s.ext ? "noopener noreferrer" : undefined}
                  data-testid={s.testid}
                  aria-label={s.name}
                  className="h-11 w-11 rounded-full bg-white/5 hover:bg-gold hover:text-black grid place-items-center transition-colors duration-300"
                >
                  <Icon size={18} />
                </a>
              );
            })}
          </div>
        </div>

        <nav className="flex flex-wrap justify-center gap-x-8 gap-y-3 py-10">
          {quick.map((q) => (
            <a key={q.label} href={q.href} className="text-sm text-gray-400 hover:text-gold transition-colors" data-testid={`footer-link-${q.label.toLowerCase().replace(/\s/g, "-")}`}>
              {q.label}
            </a>
          ))}
        </nav>

        <div className="text-center pb-8">
          <a href={`mailto:${email}`} className="text-sm text-gray-300 hover:text-gold transition-colors" data-testid="footer-email">{email}</a>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-white/10">
          <p className="text-xs text-gray-500 text-center">© 2026 Kate Mercer. All Rights Reserved.</p>
          <div className="flex gap-6 items-center">
            <a href="#" className="text-xs text-gray-500 hover:text-gold transition-colors">Privacy Policy</a>
            <a href="#" className="text-xs text-gray-500 hover:text-gold transition-colors">Terms &amp; Conditions</a>
            <a href="/admin/login" className="text-xs text-gray-600 hover:text-gold transition-colors" data-testid="footer-admin-link">Admin</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
